import React, { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import "./MijnChecks.css";
import { Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Card from "../components/Card";
import useHttp from "../hooks/use-http";
import { useTranslation } from "react-i18next";
import Chip from "@mui/material/Chip";
import ReportIcon from "@mui/icons-material/Report";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import ModuleCardCompact from "../components/ModuleCardCompact";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";
import LanguageIcon from "@mui/icons-material/Language";
import WarningIcon from "@mui/icons-material/Warning";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import BarChartIcon from "@mui/icons-material/BarChart";
import MessageIcon from "@mui/icons-material/Message";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Error from "../components/Error";
import CircleIcon from "@mui/icons-material/Circle";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StudentIconSvg = ({ sx, ...props }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 449 513"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M229.099 0.793018C225.999 0.193018 222.799 0.193018 219.699 0.793018L19.6994 40.793C8.49941 42.993 0.399414 52.893 0.399414 64.293C0.399414 74.593 6.89941 83.593 16.3994 86.893V158.693L0.699414 237.193C-0.200586 241.893 0.999414 246.793 3.99941 250.493C6.99941 254.193 11.5994 256.393 16.3994 256.393H48.3994C53.1994 256.393 57.6994 254.293 60.7994 250.493C63.8994 246.693 65.0994 241.893 64.0994 237.193L48.3994 158.693V93.593L219.699 127.893C222.799 128.493 225.999 128.493 229.099 127.893L429.099 87.893C440.299 85.593 448.399 75.693 448.399 64.293C448.399 52.893 440.299 42.993 429.099 40.793L229.099 0.793018ZM49.6994 464.293C55.4994 426.693 79.6994 395.093 112.999 379.193L176.599 450.693C181.599 456.293 187.299 460.793 193.499 464.293H49.6994ZM399.099 464.293H255.299C261.499 460.893 267.199 456.393 272.199 450.693L335.799 379.193C369.099 394.993 393.299 426.593 399.099 464.293ZM109.999 328.693C46.2994 350.293 0.399414 410.593 0.399414 481.593C0.399414 498.593 14.1994 512.293 31.0994 512.293H417.699C434.699 512.293 448.399 498.493 448.399 481.593C448.399 410.593 402.499 350.293 338.799 328.793C327.899 325.093 316.099 329.193 308.499 337.793L236.399 418.793C229.999 425.993 218.799 425.993 212.499 418.793L140.399 337.693C132.799 329.093 120.999 324.993 110.099 328.693H109.999ZM96.3994 160.293C96.3994 230.993 153.699 288.293 224.399 288.293C295.099 288.293 352.399 230.993 352.399 160.293V135.793L304.399 145.393V160.293C304.399 204.493 268.599 240.293 224.399 240.293C180.199 240.293 144.399 204.493 144.399 160.293V145.393L96.3994 135.793V160.293Z"
      fill="#439BD9"
    />
  </svg>
);

const Alert = () => {
  return <CircleIcon sx={{ color: "#E54344", width: "8px", height: "8px" }} />;
};

const Warning = () => {
  return <CircleIcon sx={{ color: "#FF8C00", width: "8px", height: "8px" }} />;
};

const NoRisk = () => {
  return <CircleIcon sx={{ color: "#28C76F", width: "8px", height: "8px" }} />;
};

const MijnChecks = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [deletingRunId, setDeletingRunId] = useState(null);

  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.between("mobile", "ipad"));

  const modules = [
    {
      id: "1",
      name: "Woning huren",
      imgPath: "img/wonen.png",
      redirectUri: "/new-check/housing/rent",
      available: true,
    },
    {
      id: "2",
      name: "Studentenwoning huren",
      imgPath: "img/huur_student.png",
      redirectUri: "/new-check/housing/rent",
      available: true,
    },
    {
      id: "3",
      name: "Woning kopen",
      imgPath: "img/woning-kopen.png",
      redirectUri: "/new-check/housing/buy",
      available: true,
    },
    {
      id: "4",
      name: "Occasion kopen",
      imgPath: "img/vervoer.png",
      available: true,
      redirectUri: "/new-check/transport/car-occasion",
    },
    {
      id: "5",
      name: "Arbeidsovereenkomst",
      imgPath: "img/werken.png",
      available: false,
      redirectUri: "/new-check/career/labour-contract",
    },
  ];

  const {
    sendRequest: getRunsRequest,
    isLoading: getRunsLoading,
    error: getRunsError,
  } = useHttp();

  const {
    sendRequest: deleteRunRequest,
    isLoading: deleteRunLoading,
    error: deleteRunError,
  } = useHttp();

  const getRuns = useCallback(() => {
    function applyData(res) {
      // Transform the date format
      res.forEach((run) => {
        const date = new Date(run.createdOn);
        const options = {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        };
        const formattedDate = date.toLocaleString("nl-NL", options);
        run.createdOn = formattedDate;
      });
      // Sort the runs by createdOn date in descending order
      const sorted = res.sort(
        (a, b) => new Date(b.createdOn) - new Date(a.createdOn)
      );
      // Set the sorted runs to state
      setRuns(sorted);
    }

    getRunsRequest(
      {
        url: `${REACT_APP_BACKEND_URL}/run/all`,
      },
      applyData
    );
  }, [getRunsRequest]);

  function deleteRun(runId) {
    setDeletingRunId(runId);

    const applyData = () => {
      // Refresh the runs list after successful deletion
      getRuns();
      setDeletingRunId(null);
    };

    deleteRunRequest(
      {
        url: `${REACT_APP_BACKEND_URL}/run/delete_run`,
        method: "DELETE",
        body: { run_id: runId },
      },
      applyData
    );
  }

  useEffect(() => {
    getRuns();
  }, [getRuns]);

  // Reset loading state if there's an error
  useEffect(() => {
    if (deleteRunError) {
      setDeletingRunId(null);
    }
  }, [deleteRunError]);

  useEffect(() => {
    let pollInterval;

    if (runs.length !== 0 && runs.filter((run) => run.isRunning).length !== 0) {
      pollInterval = setInterval(getRuns, 10000);
    } else {
      clearInterval(pollInterval);
    }

    return () => clearInterval(pollInterval);
  }, [runs, getRuns]);

  const newCheckBtn = mobile ? (
    <IconButton
      sx={{
        background: "#E27C00",
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        color: "white",
        "&:hover": {
          background: "#cf6f00",
        },
      }}
      onClick={() => navigate("/new-check")}
    >
      {" "}
      +{" "}
    </IconButton>
  ) : (
    <Button
      startIcon={mobile ? null : <AddIcon sx={{ width: "18px" }} />}
      variant="contained"
      sx={{
        background: "#E27C00",
        width: "auto",
        height: "48px",
        borderRadius: "60px",
        textTransform: "none",
        fontSize: "16px",
      }}
      onClick={() => navigate("/new-check")}
    >
      {t("newCheck")}
    </Button>
  );

  return (
    <div className="mijn-checks-container">
      {showDisclaimer && (
        <div className="dev-warning">
          <WarningIcon sx={{ color: "#439bd9", fontSize: "20px" }} />
          <div className="dev-warning-text">
            <span>{t("disclaimer")}</span>
          </div>
          <CloseIcon
            onClick={() => setShowDisclaimer(false)}
            sx={{ cursor: "pointer", fontSize: "20px" }}
          />
        </div>
      )}

      <Card
        title={
          <Box sx={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <span>{t("myChecks")}</span>
            {runs.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#DCEDFA",
                  borderRadius: "8px",
                  width: "24px",
                  height: "24px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#439BD9",
                }}
              >
                {runs.length}
              </Box>
            )}
          </Box>
        }
        subtitle={t("recentUitgevoerdeChecks")}
        extraBtn={newCheckBtn}
      >
        <div className="modules-container">
          {getRunsLoading && runs.length === 0 && (
            <div className="spinner-container">
              <CircularProgress size={"24px"} />
            </div>
          )}
          {runs.length === 0 && !getRunsLoading && !getRunsError && (
            <div className="no-runs">
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                {t("noChecks")}
              </span>
            </div>
          )}
          {getRunsError && <Error detail={t("")} />}
          {runs.length !== 0 &&
            runs.map((run) => (
              <div className="run-item-container" key={run.id}>
                <div
                  className="run-item"
                  onClick={() => {
                    navigate(`/results/${run.module.id}/${run.id}`);
                  }}
                >
                  <div className="run-module-name">
                    <span
                      style={{
                        fontSize: "15px",
                        fontWeight: "600",
                        minWidth: mobile ? "200px" : "auto",
                      }}
                    >
                      {t(`module.${run.module.name}`)}
                    </span>

                    {run?.hasError === false && (
                      <div className="risk-badges">
                        <Box
                          sx={{
                            display: "flex",
                            gap: "12px",
                          }}
                        >
                          <div className="run-result-tag high-risk">
                            <Alert />
                            <span
                              className={run?.isRunning ? "flash" : ""}
                              style={{
                                fontWeight: 700,
                                color: "#E54344",
                              }}
                            >
                              {run?.variableResults?.riskLevel2 || 0}
                            </span>
                          </div>

                          <div className="run-result-tag medium-risk">
                            <Warning />
                            <span
                              className={run?.isRunning ? "flash" : ""}
                              style={{
                                fontWeight: 700,
                                color: "#FF8C00",
                              }}
                            >
                              {run?.variableResults?.riskLevel1 || 0}
                            </span>
                          </div>

                          <div className="run-result-tag low-risk">
                            <NoRisk />
                            <span
                              className={run?.isRunning ? "flash" : ""}
                              style={{
                                fontWeight: 700,
                                color: "#28C76F",
                              }}
                            >
                              {run?.variableResults?.riskLevel0 || 0}
                            </span>
                          </div>
                        </Box>
                      </div>
                    )}

                    <div className="run-status">
                      {run?.isComplete === false &&
                        run?.request?.status !== "ERROR" && (
                          <Chip
                            color="secondary"
                            icon={
                              <CircularProgress
                                color="secondary"
                                size={12}
                                sx={{ marginRight: "4px" }}
                              />
                            }
                            label={t("Run bezig")}
                            variant="outlined"
                            sx={{
                              fontSize: "14px",
                              padding: "0px 8px",
                              fontWeight: 400,
                            }}
                          />
                        )}
                      {run?.hasError && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Chip
                            color="error"
                            icon={<ReportIcon sx={{ fontSize: "14px" }} />}
                            label={t("Fout")}
                            variant="outlined"
                            sx={{
                              fontSize: "12px",
                              fontWeight: 600,
                              padding: "0px 6px",
                              backgroundColor: "#FDEDED",
                            }}
                          />
                          {deletingRunId === run.id ? (
                            <CircularProgress
                              size={16}
                              sx={{ color: "#E54344" }}
                            />
                          ) : (
                            <DeleteIcon
                              sx={{
                                fontSize: "16px",
                                color: "#E54344",
                                cursor: "pointer",
                                "&:hover": {
                                  color: "#DC2626",
                                },
                              }}
                              onClick={() => deleteRun(run?.id)}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="uploaded-docs">
                    {run?.request?.moduleDocumentUploads?.length !== 0 &&
                      run?.request?.moduleDocumentUploads.map((doc, index) => (
                        <div className="uploaded-doc" key={index}>
                          <TaskOutlinedIcon
                            sx={{ color: "#9a9a9a", fontSize: "18px" }}
                          />
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#9a9a9a",
                              fontWeight: 400,
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              maxWidth: mobile ? "200px" : "300px",
                            }}
                          >
                            {doc?.originalFileName}
                          </span>
                        </div>
                      ))}
                    {run?.request?.licensePlate && (
                      <div className="uploaded-doc">
                        <DirectionsCarIcon
                          sx={{ color: "#439BD9", fontSize: "18px" }}
                        />
                        <span
                          style={{
                            fontSize: "10px",
                            color: "#454545",
                            fontWeight: 400,
                            border: "1px solid #439BD9",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            backgroundColor: "#F7B73160",
                            letterSpacing: "2px",
                          }}
                        >
                          {run?.request?.licensePlate}
                        </span>
                      </div>
                    )}
                    {run?.request?.isCampusContract && (
                      <div className="uploaded-doc">
                        <StudentIconSvg />
                        <span
                          style={{
                            fontSize: "14px",
                            color: "#676767",
                            fontWeight: 400,
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                          }}
                        >
                          Campuscontract (student)
                        </span>
                      </div>
                    )}

                    {run?.request?.isStudent &&
                      run?.request?.isCampusContract === false && (
                        <div className="uploaded-doc">
                          <StudentIconSvg />
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#676767",
                              fontWeight: 400,
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                            }}
                          >
                            Studentcontract
                          </span>
                        </div>
                      )}
                    {run?.request?.adminComment && (
                      <div className="uploaded-doc">
                        <MessageIcon
                          sx={{ color: "#439BD9", fontSize: "18px" }}
                        />

                        <span
                          style={{
                            fontSize: "14px",
                            color: "#676767",
                            fontWeight: 400,
                          }}
                        >
                          1 opmerking
                        </span>
                      </div>
                    )}
                    {/* {run?.request?.outputLanguage && (
                  <div className="uploaded-doc">
                    <LanguageIcon sx={{ color: "#439BD9", fontSize: "18px" }} />

                    <span
                      style={{
                        fontSize: "14px",
                        color: "#676767",
                        fontWeight: 400,
                      }}
                    >
                      {run?.request?.outputLanguage === "nl"
                        ? "Nederlands"
                        : "English"}{" "}
                    </span>
                  </div>
                )} */}

                    <div className="run-status-mobile">
                      {run?.isComplete && (
                        <span
                          style={{
                            color: "#439BD9",
                            fontSize: "16px",
                            fontWeight: "700",
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                          onClick={() => {
                            navigate(`/results/${run.module.id}/${run.id}`);
                          }}
                        >
                          {t("viewResults")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <ChevronRightIcon
                    sx={{ fontSize: "24px" }}
                  />
                </Box>
              </div>
            ))}
        </div>
      </Card>

      <Card title={t("ourModules")} subtitle={t("onzeModulesText")}>
        <div className="modules-container">
          {modules.map((module) => (
            <div key={module?.id}>
              <ModuleCardCompact module={module} />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default MijnChecks;
